import type { OrganizationResourceApi } from '@ingenico/api-sdk-provisioning-public';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { ListViewItem, useRenderDefaultColumHeader } from '@ingenico/react-ui/datagrid';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseAppApplicationOrganizationsTabColumnsReturn } from '../components/device-app-organizations-tab/DeviceAppOrganizationsTab.model';
import type { OrganizationsRows } from '../pages/os-application-details-page/OSApplicationDetailsPage.model';
import { OSOrganizationsTabMobileRow } from '../components/os-organizations-tab-mobile-row/OSOrganizationsTabMobileRow.ctrl';

export function useAppApplicationOrganizationsTabColumns(): UseAppApplicationOrganizationsTabColumnsReturn {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.DEVICE_APPS, ALL_LNGS_NAMESPACES.ORGANIZATION]);

  const renderNameCell = useCallback(
    (params: GridRenderCellParams<OrganizationsRows, OrganizationsRows>): ReactNode => {
      const { name } = params.row;
      return <ListViewItem primaryText={name} />;
    },
    [],
  );

  const { defaultRenderColumnHeader } = useRenderDefaultColumHeader(t);

  const renderOperatingCountryCellValue = useCallback(
    (params: GridRenderCellParams<OrganizationResourceApi, OrganizationResourceApi>): ReactNode => {
      const operatingCountry = t(`field.country.${params?.row?.location?.country}`, { ns: ALL_LNGS_NAMESPACES.COMMON });
      return <ListViewItem primaryText={operatingCountry} />;
    },
    [t],
  );

  const renderLocationCellValue = useCallback(
    ({ row }: GridRenderCellParams<OrganizationResourceApi, OrganizationResourceApi>): ReactNode => {
      return (
        <ListViewItem
          primaryText={`${row?.location?.zipCode ?? ''} ${row?.location?.city ?? ''}`}
          secondaryText={row?.location?.address ?? ''}
        />
      );
    },
    [],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        renderHeader: defaultRenderColumnHeader('field.name', ALL_LNGS_NAMESPACES.DEVICE_APPS),
        headerName: t('field.name', { ns: ALL_LNGS_NAMESPACES.DEVICE_APPS }),
        filterable: false,
        flex: 1,
        renderCell: renderNameCell,
      },
      {
        field: 'location.country',
        renderHeader: defaultRenderColumnHeader('organization.location.country', ALL_LNGS_NAMESPACES.ORGANIZATION),
        headerName: t('organization.location.country', { ns: ALL_LNGS_NAMESPACES.ORGANIZATION }),
        filterable: false,
        flex: 1,
        renderCell: renderOperatingCountryCellValue,
      },
      {
        field: 'location',
        renderHeader: defaultRenderColumnHeader('field.location.label', ALL_LNGS_NAMESPACES.COMMON),
        headerName: t('field.location.label', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        filterable: false,
        flex: 1,
        renderCell: renderLocationCellValue,
      },
    ],
    [defaultRenderColumnHeader, t, renderNameCell, renderLocationCellValue, renderOperatingCountryCellValue],
  );

  const defaultAvailableColumns = columns.map((column) => column.field);

  const renderMobileListViewColumn = useCallback(
    ({ row }: GridRenderCellParams<OrganizationResourceApi, OrganizationResourceApi>): ReactNode => (
      <OSOrganizationsTabMobileRow row={row} />
    ),
    [],
  );

  return { columns, defaultAvailableColumns, renderMobileListViewColumn };
}
